import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useStarData } from '@/hooks/useStarData';
import { useThemeManager } from '@/hooks/useThemeManager';
import { ThemeName } from '@/utils/storage';
import ThemeSelector from '@/components/layout/ThemeSelector';
import { toast } from 'sonner';

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { data, updateData } = useStarData();
  const { applyTheme } = useThemeManager(data?.theme);

  const handleThemeChange = async (theme: ThemeName) => {
    if (!data) return;

    try {
      applyTheme(theme);

      const updatedData = {
        ...data,
        theme,
      };

      await updateData(updatedData);
    } catch (error) {
      console.error('Error updating theme:', error);
      toast.error('Failed to update theme');
    }
  };

  const handleToggleEditMode = () => {
    // Navigate back to main page with edit mode enabled
    navigate('/', { state: { toggleEditMode: true } });
  };


  if (!data) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center">
        <div className="animate-pulse-scale">
          <div className="text-4xl">⭐</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      {/* Simplified Header */}
      <header className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-200/50 dark:border-gray-700/50 pt-[env(safe-area-inset-top,0px)] pl-[env(safe-area-inset-left,0px)] pr-[env(safe-area-inset-right,0px)]">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl"
            >
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Settings
              </h1>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Customize your experience
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 pb-[calc(2rem+env(safe-area-inset-bottom,0px))] pt-12">
        <div className="grid gap-8 md:grid-cols-2">
          
          {/* Theme Selection */}
          <div className="md:col-span-2">
            <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-md rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50">
              <ThemeSelector
                currentTheme={data.theme}
                onThemeChange={handleThemeChange}
              />
            </div>
          </div>

          {/* Parent Controls */}
          <div className="md:col-span-2 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md rounded-2xl p-8 shadow-lg border border-gray-200/50 dark:border-gray-700/50">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Parent Controls</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm leading-relaxed">
              Access advanced settings, manage content, and sign out of your account. 
              Requires parent authentication.
            </p>
            
            <Button 
              onClick={handleToggleEditMode}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white border-0 py-3 rounded-xl font-semibold"
            >
              Open Parent Controls
            </Button>
            
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 text-center">
              💡 Sign out option available in Parent Controls
            </p>
          </div>

        </div>
      </main>
    </div>
  );
};

export default SettingsPage;